import { Config } from '@genial/config';
import { ResponsesPensionOfferResponse } from '@genial/services/dist/models';
import { PensionOfferDetailsSuccessResponse } from '@genial/services/src';
import { Utils } from '@genial/utils';

import { PrivatePensionPaymentMethod } from '../../../pages/BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import {
  ReturnTypeOf_ClienteListarDadosBancarios,
  ReturnTypeOf_ObterDetalhesFundoPrevidenciaPorCnpjFundo,
  ReturnTypeOf_ObterListaBancosSeguradora,
  ReturnTypeOf_PrevidenciaObterListaParentescoSeguradora,
  ReturnTypeOf_PrevidenciaObterListaSeguradora,
  ReturnTypeOf_PrevidenciaObterParametrosPrevidencia,
  ReturnTypeOf_ProdutosObterProdutosDisponiveis,
} from '../external/types';
import {
  DynamicCustomFiltersProps,
  PrivatePensionBankEntityNormalized,
  PrivatePensionClientBankAccountEntityNormalized,
  PrivatePensionDetailsNormalized,
  PrivatePensionDueDateOption,
  PrivatePensionEntityNormalized,
  PrivatePensionInsuranceCompanyParametersEntityNormalized,
  PrivatePensionInsuranceEntityNormalized,
  PrivatePensionRelatedPartyEntityNormalized,
} from '../types';

import { filterHasOptions } from './index';

export const normalizePrivatePension = (
  data: ReturnTypeOf_ProdutosObterProdutosDisponiveis
): PrivatePensionEntityNormalized[] => {
  if (!data || !data.ativosPrevidencia) {
    return [];
  }

  try {
    return data.ativosPrevidencia.map((item) => {
      const managerFirstName = (item.nomeGestora || '').trim().split(' ')[0].toLowerCase();
      const managerImage = `${Config.GenialCdnUrl}Fundos/Gestora/${managerFirstName}_logo_grande.png`;

      return {
        privatePensionId: item.produtoId,
        productNameNormalized: item.nomeFundo?.trim(),
        productTypeNormalized: item.tipoFundo,
        productDocument: item.cnpjFundo,
        monthlyRentability: item.valorMes,
        monthlyRentabilityNormalized: Utils.Mask.Percentage(item.valorMes),
        annualRentability: item.valorAno,
        annualRentabilityNormalized: Utils.Mask.Percentage(item.valorAno),
        twelveMonthsRentability: item.valor12MesesAtras,
        twelveMonthsRentabilityNormalized: Utils.Mask.Percentage(item.valor12MesesAtras),
        minimumApplication: item.aplicacaoMinima,
        minimumApplicationNormalized: Utils.Mask.Money(item.aplicacaoMinima),
        monthlyMinimumApplication: item.aplicacaoMinimaMensal,
        monthlyMinimumApplicationNormalized: Utils.Mask.Money(item.aplicacaoMinimaMensal),
        managerNameNormalized: item.nomeGestora,
        managerImage,
        valueCurrentQuotation: item.valorCotaAtual,
        valueCurrentQuotationNormalized: Utils.Mask.Money(item.valorCotaAtual),
        valueDaily: item.valorDiaria,
        valueDailyNormalized: Utils.Mask.Money(item.valorDiaria),
        insuranceCompanyId: item.idSeguradora,
        fundProfileNormalized: item.perfilFundo,
        initialDateTime: item.dataInicio ? new Date(item.dataInicio).getTime() : 0,
        initialDateNormalized: item.dataInicio
          ? Utils.Mask.DateFormat(new Date(item.dataInicio), 'dd/MM/yyyy')
          : '-',
        referenceDateTime: item.dataReferencia ? new Date(item.dataReferencia).getTime() : 0,
        referenceDateNormalized: item.dataReferencia
          ? Utils.Mask.DateFormat(new Date(item.dataReferencia), 'dd/MM/yyyy')
          : '-',
      };
    });
  } catch (_error) {
    console.log({ _error });

    return [];
  }
};

export const normalizePrivatePensionOffers = (
  data?: ResponsesPensionOfferResponse[] | null
): PrivatePensionEntityNormalized[] => {
  if (!data) {
    return [];
  }

  return data.map(({ offer }) => {
    const cutoffTime = new Date();

    if (offer.fundCutoffTime) {
      const [hours, minutes] = offer.fundCutoffTime.split(':');
      cutoffTime.setHours(Number(hours || 0));
      cutoffTime.setMinutes(Number(minutes || 0));
    }

    const managerFirstName = (offer.managerName || '').trim().split(' ')[0].toLowerCase();
    const managerImage = `${Config.GenialCdnUrl}Fundos/Gestora/${managerFirstName}_logo_grande.png`;

    return {
      privatePensionId: offer.productId,
      productNameNormalized: offer.fundName?.trim() || '',
      productTypeNormalized: offer.fundTypeDescription || '',
      productDocument: offer.fundCnpj || '',
      monthlyRentability: offer.profitabilityMonthly,
      monthlyRentabilityNormalized: Utils.Mask.Percentage(offer.profitabilityMonthly),
      annualRentability: offer.profitabilityYearly,
      annualRentabilityNormalized: Utils.Mask.Percentage(offer.profitabilityYearly),
      twelveMonthsRentability: offer.profitability12Months,
      twelveMonthsRentabilityNormalized: Utils.Mask.Percentage(offer.profitability12Months),
      minimumApplication: offer.minimumApplication,
      minimumApplicationNormalized: Utils.Mask.Money(offer.minimumApplication),
      monthlyMinimumApplication: offer.minimumMonthlyApplication,
      monthlyMinimumApplicationNormalized: Utils.Mask.Money(offer.minimumMonthlyApplication),
      cutoffTime,
      cutoffTimeNormalized: offer.fundCutoffTime || '',
      managerNameNormalized: offer.managerName || '',
      managerImage,
      valueCurrentQuotation: offer.currentShareValue,
      valueCurrentQuotationNormalized: Utils.Mask.Money(offer.currentShareValue),
      valueDaily: offer.profitabilityDaily,
      valueDailyNormalized: Utils.Mask.Money(offer.profitabilityDaily),
      insuranceCompanyId: offer.insuranceId,
      fundProfileNormalized: offer.riskProfileDescription || '',
      initialDateTime: offer.fundStartDate ? new Date(offer.fundStartDate).getTime() : 0,
      initialDateNormalized: offer.fundStartDate
        ? Utils.Mask.DateFormat(new Date(offer.fundStartDate), 'dd/MM/yyyy')
        : '-',
      referenceDateTime: offer.profitabilityReferenceDate
        ? new Date(offer.profitabilityReferenceDate).getTime()
        : 0,
      referenceDateNormalized: offer.profitabilityReferenceDate
        ? Utils.Mask.DateFormat(new Date(offer.profitabilityReferenceDate), 'dd/MM/yyyy')
        : '-',
    };
  });
};

export const normalizePrivatePensionFilters = (
  data?: PrivatePensionEntityNormalized[] | null
): DynamicCustomFiltersProps => {
  if (!data || !data.length) {
    return { filters: [] };
  }

  const subCategoryFilter = {
    fieldName: 'productTypeNormalized',
    label: 'Subcategoria',
    options: Array.from(new Set(data.map((item) => item.productTypeNormalized))).map((label) => ({
      label,
      value: label,
    })),
  };

  const minimumApplicationFilter = {
    fieldName: 'legacyMonthlyMinApplication',
    label: 'Valor de contribuição mensal',
    options: Array.from(new Set(data.map((item) => item.monthlyMinimumApplicationNormalized)))
      .sort((a, b) => {
        const valueA = Utils.Unmask.Money(a);
        const valueB = Utils.Unmask.Money(b);
        return valueA - valueB;
      })
      .map((label) => ({
        label,
        value: Utils.Unmask.Money(label).toString(),
      })),
  };

  const filtersWithValues = [subCategoryFilter, minimumApplicationFilter].filter(filterHasOptions);

  return { filters: filtersWithValues };
};

export const normalizePrivatePensionInsuranceList = (
  data: ReturnTypeOf_PrevidenciaObterListaSeguradora
): PrivatePensionInsuranceEntityNormalized[] => {
  if (!data || !data.retorno.length) {
    return [];
  }

  return data.retorno.map((item) => {
    return {
      insuranceId: item.idSeguradora,
      insuranceName: item.nmSeguradora,
      insuranceFlag: item.flgSeguradora,
      insuranceServiceId: item.idServico,
      insuranceCnpj: item.nrCnpj,
      insuranceCnpjNormalized: Utils.Mask.Cnpj(item.nrCnpj.toString()),
      insuranceCode: item.cdSeguradora,
    };
  });
};

export const normalizePrivatePensionRelatedPartyList = (
  data: ReturnTypeOf_PrevidenciaObterListaParentescoSeguradora
): PrivatePensionRelatedPartyEntityNormalized[] => {
  if (!data || !data.retorno.length) {
    return [];
  }

  return data.retorno.map((item) => {
    return {
      insuranceCompanyId: item.idSeguradora,
      relatedPartyId: item.idParentesco,
      name: item.dsParentesco,
    };
  });
};

export const normalizePrivatePensionBankList = (
  data: ReturnTypeOf_ObterListaBancosSeguradora
): PrivatePensionBankEntityNormalized[] => {
  if (!data || !data.retorno.length) {
    return [];
  }

  return data.retorno.map((item) => {
    return {
      bankId: item.idBanco,
      insuranceCompanyId: item.idSeguradora,
      name: item.nmBanco,
      bankNumber: item.nuBanco,
    };
  });
};

export const normalizePrivatePensionClientBankAccountList = (
  data: ReturnTypeOf_ClienteListarDadosBancarios
): PrivatePensionClientBankAccountEntityNormalized[] => {
  if (!data || !data.retorno.length) {
    return [];
  }

  return data.retorno.map((item) => {
    return {
      bankAccountId: item.idDadosBancarios,
      bankCode: item.codigoBanco,
      bankName: item.nomeBanco,
      agencyNumber: item.numeroAgencia,
      agencyDigit: item.digitoAgencia,
      accountNumber: item.numeroContaCorrente,
      accountDigit: item.digitoContaCorrente,
    };
  });
};

const bankFieldRuleNames = {
  1: 'bankAgencyNumber',
  2: 'bankAgencyDigit',
  3: 'bankAccountNumber',
  4: 'bankAccountDigit',
};

export const normalizeBankCode = (bankCode?: string | number | null): string => {
  const numericBankCode = Number(bankCode);
  return Number.isNaN(numericBankCode) ? '' : numericBankCode.toString();
};

const mapDueDateOptions = (
  dueDates: { idVencimento: number; nuVencimento: number }[] = []
): PrivatePensionDueDateOption[] =>
  dueDates.map((dueDate) => ({
    value: dueDate.nuVencimento.toString(),
    label: dueDate.nuVencimento.toString(),
  }));

export const normalizePrivatePensionInsuranceCompanyParameters = (
  data: ReturnTypeOf_PrevidenciaObterParametrosPrevidencia,
  insuranceCompanyId: number
): PrivatePensionInsuranceCompanyParametersEntityNormalized => {
  return {
    banks: data.bancos.map((bank) => ({
      bankId: bank.idBanco,
      insuranceCompanyId,
      name: `${bank.codgoBanco} - ${bank.nmBanco}`,
    })),
    bankValidationSettings: data.bancos
      .map((bank) => {
        const rules = bank.configuracoes
          .map((configuration) => ({
            [bankFieldRuleNames[configuration.tipo]]: {
              required: configuration.obrigatorio,
              minLength: configuration.tamanhoMinimo,
              maxLength: configuration.tamanhoMaximo,
            },
          }))
          .reduce((acc, val) => ({ ...acc, ...val }), {});

        return { [bank.idBanco]: rules };
      })
      .reduce((acc, val) => ({ ...acc, ...val }), {}),
    maxBeneficiaries: data.limiteBeneficiarios,
    boletoDueDates: mapDueDateOptions(data.boleto?.vencimentos),
    debitDueDatesByBankCode: data.bancos.reduce((accumulator, bank) => {
      const bankCode = normalizeBankCode(bank.codgoBanco);
      if (!bankCode) {
        return accumulator;
      }
      const debitPaymentMethod = bank.formasPagamento?.find(
        (paymentMethod) => paymentMethod.idFormaPagamento === PrivatePensionPaymentMethod.Debit
      );
      const dueDateOptions = mapDueDateOptions(debitPaymentMethod?.vencimentos);
      // Omit empty entries so consumers can use `?? defaultDebitDueDates`.
      if (dueDateOptions.length) {
        accumulator[bankCode] = dueDateOptions;
      }
      return accumulator;
    }, {} as Record<string, PrivatePensionDueDateOption[]>),
  };
};

export const normalizePrivatePensionDetails = (
  data: ReturnTypeOf_ObterDetalhesFundoPrevidenciaPorCnpjFundo
): PrivatePensionDetailsNormalized => {
  if (!data) {
    return { documents: [] };
  }

  return {
    documents: data.docsPrevidencia.map((doc) => ({
      fileName: doc.tipoDoc.tipoProdutoDoc || 'Documento sem nome',
      fileUrl: doc.urlDocumento,
      fileAvailable: !!doc.urlDocumento,
    })),
  };
};

export const normalizePrivatePensionOfferDetails = (
  data: PensionOfferDetailsSuccessResponse
): PrivatePensionDetailsNormalized => {
  if (!data || !data.data || !data.data.pensionDocuments) {
    return { documents: [] };
  }

  return {
    documents: data.data.pensionDocuments.map((doc) => ({
      fileName: doc.documentType?.productDocumentType || 'Documento sem nome',
      fileUrl: doc.documentUrl || '',
      fileAvailable: !!doc.documentUrl || !!doc.fileImage,
      fileImage: doc.fileImage || undefined,
    })),
  };
};

export const normalizePrivatePensionAvailableProductsFilters = (
  data?: PrivatePensionEntityNormalized[] | null
): DynamicCustomFiltersProps => {
  if (!data || !data.length) {
    return { filters: [] };
  }

  const subCategoryFilter = {
    fieldName: 'productTypeNormalized',
    label: 'Subcategoria',
    options: Array.from(new Set(data.map((item) => item.productTypeNormalized))).map((label) => ({
      label,
      value: label,
    })),
  };

  const filtersWithValues = [subCategoryFilter].filter(filterHasOptions);

  return { filters: filtersWithValues };
};
